import { useTranslation } from '@thoughtspot/i18n';
import { Modal } from '@thoughtspot/radiant-react/widgets/modal';
import _ from 'lodash';
import React, { useEffect, useReducer, useRef, useState, } from 'react';
import { NasStatus, SnapshotStatus, } from "../../../services/generated/graphql-types";
import { useGetStatus } from "../../../services/sidebar/sidebar-service";
import { useDeleteNasMutation, useDeleteSnapshotMutation, useGetNasQuery, useGetSnapshotQuery, useSetNasMutation, useSetSnapshotMutation, } from "../../../services/system/system-service";
import { GenericError } from '../../generic-error/generic-error.container';
import { Content, Header, Seperator, WithQueryHandler } from '../common';
import { ActionButtons, LearnMore, Mode } from '../common/constants';
import { getOrgViewMode, parseFormData } from '../common/utils';
import styles from './backup-storage.module.scss';
import { actions, initialSnapshotConfig, nasInititalConfig, reducer, } from './common/backup-storage.util';
import { Action, BackupAndStorageType, pageName, TranslationsKeys, } from './common/constants';
import { NasViewSection } from './nas/nas';
import { SnapshotViewSection } from './snapshot/snapshot';
const POLLING_TIME_OUT = 5000;
const BackupStorageContainer = ({ onSuccess, onError, showLoader, hideLoader, currentOrgId, }) => {
    const { t } = useTranslation();
    const [mode, setMode] = useState(Mode.VIEW);
    const [currentStorageType, setCurrentStorageType] = useState(BackupAndStorageType.NAS);
    const [snapshotPayload, setSnapshotPayload] = useState(null);
    const [nasPayload, setNasPayload] = useState(null);
    const [snapShotTableData, setsnapShotTableData] = useState(null);
    const [nasTableData, setNasTableData] = useState(null);
    const [snapShotName, setSnapShot] = useState('');
    const [nasMountPoint, setNasMountPoint] = useState('');
    const [isNasModalOpen, setIsNasModalOpen] = useState(false);
    const [isSnapshotModalOpen, setIsSnapshotModalOpen] = useState(false);
    const [showCheckLaterModal, setShowCheckLaterModal] = useState(false);
    const [updateTable, setUpdateTable] = useState(true);
    const [nasConfigState, dispatch] = useReducer(reducer, nasInititalConfig()[0]);
    const [snapshotConfigState, dispatchSnapshot] = useReducer(reducer, initialSnapshotConfig()[0]);
    const isPolling = useRef(false);
    const { data, loading, error } = useGetStatus(getOrgViewMode(currentOrgId));
    const isNasEnabled = data?.Data?.status?.system?.backup_storage?.nas;
    const isSnapshotEnabled = data?.Data?.status?.system?.backup_storage?.snapshot;
    const { getNas, data: nasResponse, loading: isNasLoading, error: nasGetQueryError, } = useGetNasQuery();
    const { getSnapshot, data: snapshotResponse, loading: isSnapshotLoading, error: snapshotGetQueryError, } = useGetSnapshotQuery();
    const resetNasForm = () => {
        dispatch({
            type: Action.RESET_NAS_FORM,
            payload: nasConfigState.formFields,
        });
    };
    const resetSnapshotForm = () => {
        dispatchSnapshot({
            type: Action.RESET_SNAPSHOT_FORM,
            payload: snapshotConfigState.formFields,
        });
    };
    useEffect(() => {
        resetNasForm();
        resetSnapshotForm();
        getNas();
        getSnapshot();
    }, []);
    useEffect(() => {
        if (!isPolling.current &&
            (loading || isNasLoading || isSnapshotLoading)) {
            showLoader();
        }
        else {
            hideLoader();
        }
    }, [loading, isNasLoading, isSnapshotLoading]);
    const displayEmptyNasState = () => {
        dispatch({
            type: Action.EMPTY_NAS,
        });
    };
    const displayNonEmptyNasState = () => {
        dispatch({
            type: Action.NOT_EMPTY_NAS,
        });
    };
    const resetForm = (form) => {
        if (form === BackupAndStorageType.SNAPSHOT) {
            return resetSnapshotForm();
        }
        resetNasForm();
    };
    const decideNasState = (nasTable) => nasTable?.length > 0
        ? displayNonEmptyNasState()
        : displayEmptyNasState();
    const isNasPollingNeeded = (_data) => {
        let needed = false;
        if (_.isEmpty(_data)) {
            return needed;
        }
        _data.forEach((nas) => {
            if (nas.status === NasStatus.NotConfigured) {
                needed = true;
            }
        });
        return needed;
    };
    const setNasPollingIfNeeded = (_data) => {
        if (isNasPollingNeeded(_data)) {
            isPolling.current = true;
            setTimeout(getNas, POLLING_TIME_OUT);
        }
    };
    const isSnapshotPollingNeeded = (_data) => {
        let needed = false;
        if (_.isEmpty(_data))
            return needed;
        _data.forEach((snapshot) => {
            if (snapshot.snapshotStatus ===
                SnapshotStatus.SnapshotCreateStarted ||
                snapshot.snapshotStatus ===
                    SnapshotStatus.SnapshotDeleteScheduled) {
                needed = true;
            }
        });
        return needed;
    };
    const setSnapshotPollingIfNeeded = (_data) => {
        if (isSnapshotPollingNeeded(_data)) {
            isPolling.current = true;
            setTimeout(getSnapshot, POLLING_TIME_OUT);
        }
    };
    useEffect(() => {
        if (nasResponse && nasResponse.Data && !nasResponse.Error) {
            decideNasState(nasResponse.Data ?? []);
            setNasTableData(nasResponse);
        }
        else if (nasResponse && !isNasLoading && nasResponse.Error) {
            onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
        }
    }, [nasResponse]);
    const onErr = () => {
        hideLoader();
        onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
    };
    const onCompleteDeleteSnapShot = (updatedSnapShots) => {
        hideLoader();
        try {
            if (!updatedSnapShots.Error) {
                setsnapShotTableData(updatedSnapShots);
                setSnapshotPollingIfNeeded(updatedSnapShots.Data);
                setShowCheckLaterModal(true);
            }
        }
        catch (err) {
            onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
        }
    };
    const onCompleteDeleteNas = (updatedNas) => {
        hideLoader();
        try {
            if (!updatedNas.Error) {
                setNasTableData(updatedNas);
                onSuccess(t(TranslationsKeys.NAS_DELETED));
                decideNasState(updatedNas?.Data ?? []);
            }
        }
        catch (err) {
            onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
        }
    };
    const [deleteSnapShot] = useDeleteSnapshotMutation(onCompleteDeleteSnapShot, onErr, snapShotName);
    const [deleteNas] = useDeleteNasMutation(onCompleteDeleteNas, onErr, {
        mount_point: nasMountPoint,
    });
    const deleteNasRowClickHandler = (mountPoint) => {
        setIsNasModalOpen(true);
        setNasMountPoint(mountPoint);
    };
    const deleteSnapshotRowClickHandler = (snapshotName) => {
        setSnapShot(snapshotName);
        setIsSnapshotModalOpen(true);
    };
    useEffect(() => {
        if (snapshotResponse &&
            snapshotResponse.Data &&
            !snapshotResponse.Error) {
            setsnapShotTableData(snapshotResponse);
            setSnapshotPollingIfNeeded(snapshotResponse.Data);
        }
        else if (snapshotResponse && snapshotResponse.Error) {
            onError(t(TranslationsKeys.MESSAGE_API_FAILURE));
        }
    }, [snapshotResponse]);
    const onSaveSuccess = (response) => {
        const newData = response?.Data;
        hideLoader();
        if (newData && !response.Error) {
            setMode(Mode.VIEW);
            resetForm(currentStorageType);
            if (currentStorageType === BackupAndStorageType.NAS) {
                setNasPayload(null);
                setNasTableData(response);
                setNasPollingIfNeeded(response.Data);
                onSuccess(t(TranslationsKeys.TOAST_SUCCESS));
            }
            else if (currentStorageType === BackupAndStorageType.SNAPSHOT) {
                setsnapShotTableData(response);
                setSnapshotPollingIfNeeded(response.Data);
                setSnapshotPayload(null);
                setShowCheckLaterModal(true);
            }
        }
        else {
            onError(response.Error);
        }
    };
    const errorHandlerForMutation = (errMsg) => {
        hideLoader();
        if (typeof errMsg === 'string') {
            onError(errMsg);
        }
        else {
            onError(t(TranslationsKeys.MESSAGE_SAVE_FAILURE));
        }
    };
    const [updateSnapshot] = useSetSnapshotMutation(response => onSaveSuccess(response), graphQlError => errorHandlerForMutation(graphQlError), snapshotPayload);
    const [updateNas] = useSetNasMutation(response => onSaveSuccess(response), graphQlError => errorHandlerForMutation(graphQlError), nasPayload);
    useEffect(() => {
        async function fetchNas() {
            if (nasPayload) {
                await updateNas();
                setUpdateTable(true);
            }
        }
        fetchNas();
    }, [nasPayload]);
    useEffect(() => {
        async function fetchSnapshot() {
            if (snapshotPayload) {
                await updateSnapshot();
                setUpdateTable(true);
            }
        }
        fetchSnapshot();
    }, [snapshotPayload]);
    const validateForm = (currentFormData = {}) => {
        let anyInValid = false;
        let isValid = false;
        currentFormData.forEach((i) => {
            if (i && i.valid === false) {
                anyInValid = true;
            }
        });
        if (!anyInValid) {
            if (currentStorageType === BackupAndStorageType.NAS) {
                isValid = true;
            }
            const snapshotFormName = currentFormData.find((form) => form.id === 'name');
            if (snapshotFormName) {
                if (snapshotResponse && snapshotResponse.Data) {
                    const snapShotData = snapshotResponse.Data;
                    let isDuplicateName = false;
                    if (snapShotData && snapShotData.length > 0) {
                        snapShotData.forEach((snapshot) => {
                            if (snapshot.name === snapshotFormName.value) {
                                isDuplicateName = true;
                            }
                        });
                    }
                    if (isDuplicateName) {
                        const duplicatNameError = t(TranslationsKeys.SNAPSHOT_DUPLICATE_NAME_ERROR);
                        isValid = false;
                        dispatchSnapshot({
                            type: Action.VALIDATE_SNAPSHOT_FORM_DUPLICATE_NAME,
                            payload: currentFormData,
                            errorMessage: duplicatNameError,
                        });
                    }
                    isValid = true;
                }
            }
        }
        return isValid;
    };
    const submitSnapshotData = (formFields) => {
        const snapshotFormValue = {};
        for (let index = 0; index < formFields.length; index++) {
            const form = formFields[index];
            if (form.id === 'ttl') {
                snapshotFormValue[form.id] = Number(form.value);
            }
            else {
                snapshotFormValue[form.id] = form.value;
            }
        }
        setSnapshotPayload(snapshotFormValue);
    };
    const submitNasData = (formFields) => {
        const nasFormValue = parseFormData(formFields);
        setNasPayload(nasFormValue);
    };
    const onSave = (currentFormData) => {
        const isFormOverallValid = validateForm(currentFormData);
        if (isFormOverallValid) {
            showLoader();
            if (currentStorageType === BackupAndStorageType.NAS) {
                submitNasData(currentFormData);
            }
            else if (currentStorageType === BackupAndStorageType.SNAPSHOT) {
                submitSnapshotData(currentFormData);
            }
            setUpdateTable(false);
        }
    };
    const addNasMountPointClickHandler = () => {
        dispatch({
            type: Action.REMOVE_USERNAME_PASSWORD,
            payload: nasConfigState.formFields,
        });
        setCurrentStorageType(BackupAndStorageType.NAS);
        setMode(Mode.EDIT);
    };
    const addSnapshotClickHandler = () => {
        setCurrentStorageType(BackupAndStorageType.SNAPSHOT);
        setMode(Mode.EDIT);
    };
    const actionHandler = (type, currentFormData = {}) => {
        switch (type) {
            case ActionButtons.EDIT_NAS:
                setMode(Mode.EDIT);
                setCurrentStorageType(BackupAndStorageType.NAS);
                break;
            case ActionButtons.EDIT_SNAPSHOT:
                setCurrentStorageType(BackupAndStorageType.SNAPSHOT);
                break;
            case ActionButtons.BACK:
            case ActionButtons.CANCEL:
                dispatch({
                    type: Action.REMOVE_USERNAME_PASSWORD,
                    payload: currentFormData,
                });
                resetForm(currentStorageType);
                setMode(Mode.VIEW);
                break;
            case ActionButtons.SAVE:
                onSave(currentFormData);
                break;
            default:
                setMode(Mode.VIEW);
                break;
        }
    };
    const formEditHandler = (currentFormData, id) => {
        if (id === 'mount_type') {
            const mountTypeData = currentFormData.find((form) => form.id === id);
            if (mountTypeData.value === 'nfs') {
                dispatch({
                    type: Action.REMOVE_USERNAME_PASSWORD,
                    payload: currentFormData,
                });
            }
            else {
                dispatch({
                    type: Action.ADD_USERNAME_PASSWORD,
                    payload: currentFormData,
                });
            }
        }
        else {
            validateForm(currentFormData);
        }
    };
    const onDeleteNasConfirm = () => {
        setIsNasModalOpen(false);
        deleteNas();
        showLoader();
    };
    const onDeleteSnapshotConfirm = () => {
        setIsSnapshotModalOpen(false);
        deleteSnapShot();
        showLoader();
    };
    const renderGenricErrorScreen = () => React.createElement(GenericError, null);
    const renderContent = () => (React.createElement(React.Fragment, null, mode === Mode.VIEW ? (React.createElement("div", { className: styles.backupViewContainer },
        React.createElement(Header, { title: t(TranslationsKeys.BACKUPANDSTORAGE_TITLE), titleSize: "large", isFullWidth: true, learnMoreKey: LearnMore.NAS, mode: mode }),
        nasGetQueryError
            ? renderGenricErrorScreen()
            : isNasEnabled && (React.createElement(React.Fragment, null,
                React.createElement(Seperator, null),
                React.createElement(NasViewSection, { table: nasTableData?.Data ?? [], configState: nasConfigState, isModalOpen: isNasModalOpen, addClickHandler: addNasMountPointClickHandler, rowDeleteClickHandler: deleteNasRowClickHandler, confirmDeleteHandler: onDeleteNasConfirm, dismissModalHandler: () => setIsNasModalOpen(false), updateTable: updateTable }))),
        snapshotGetQueryError
            ? renderGenricErrorScreen()
            : isSnapshotEnabled && (React.createElement(React.Fragment, null,
                React.createElement(Seperator, null),
                React.createElement(SnapshotViewSection, { table: snapShotTableData?.Data ?? [], configState: snapshotConfigState, isModalOpen: isSnapshotModalOpen, addClickHandler: addSnapshotClickHandler, rowDeleteClickHandler: deleteSnapshotRowClickHandler, confirmDeleteHandler: onDeleteSnapshotConfirm, dismissModalHandler: () => setIsSnapshotModalOpen(false), updateTable: updateTable }))),
        React.createElement(Modal, { title: t(TranslationsKeys.CHECK_LATER_TITLE), isOpen: showCheckLaterModal, size: 'small', confirmButton: {
                text: t(TranslationsKeys.CHECK_LATER_CTA),
                onConfirm: () => {
                    setShowCheckLaterModal(false);
                },
            } },
            React.createElement("div", null, t(TranslationsKeys.CHECK_LATER_BODY))))) : (React.createElement(Content, { key: `${currentStorageType}_${mode}`, config: currentStorageType === BackupAndStorageType.NAS
            ? nasConfigState
            : snapshotConfigState, backConfirm: true, actions: actions, actionHandler: actionHandler, mode: mode, onEdit: formEditHandler, pageName: pageName, isFullWidth: true }))));
    return React.createElement(React.Fragment, null, error ? renderGenricErrorScreen() : renderContent());
};
export const BackupStorage = (props) => (React.createElement(WithQueryHandler, null,
    React.createElement(BackupStorageContainer, { ...props })));
//# sourceMappingURL=backup-storage.container.js.map